/**
 * Effective ("Last updated") dates for the binding legal documents on the
 * marketing Trust Center — single source of truth so every legal page renders a
 * dated freshness marker a procurement reviewer can cite.
 *
 * Dates are ISO (`YYYY-MM-DD`). A document's date is bumped in the SAME change
 * that alters its substantive copy; the value is the date of that last
 * substantive revision (git-evidenced). Pages whose copy was not touched in a
 * given sweep keep the date of their last real change.
 *
 * Pure data + a pure lookup — no React, no I/O — so pages, tests, and the
 * prerenderer can all read it.
 */

export type LegalDocSlug =
  | 'privacy'
  | 'terms'
  | 'dpa'
  | 'cookies'
  | 'refunds'
  | 'ai-compliance'
  | 'disclosure';

/**
 * The last-substantive-revision date per legal document.
 *
 * `privacy` reads `2026-08-17`: the legal-document review of that date corrected
 * two statements on the page — the signed-out-visitor section described a
 * default-ON processing operation as if a workspace had to opt in (the feature
 * registry has `defaultEnabled: true`, and the section's own "switch it off"
 * sentence already assumed otherwise), and the page description advertised a
 * 7-day retention tier the published table does not contain. A privacy notice
 * that understates a default is the kind of drift this date exists to date.
 *
 * `terms` reads `2026-08-17`: the protection audit of that date changed two
 * figures the page quotes from the MSA — the liability-cap floor (USD 100,000 →
 * USD 25,000, MSA § 16.1) and the uptime commitment (99.9%/99.95% → 99.5%/99.9%,
 * MSA § 6.1 + `docs/legal/sla.md`). Both are substantive commercial terms, so the
 * date moves with them.
 *
 * The remaining entries read `2026-07-02`: the content-correctness sweep of that
 * date touched ai-compliance directly, and the other documents' last
 * git-evidenced substantive change lands on the same date (repository import
 * boundary). The 2026-08-17 review changed the SOURCE instruments in
 * `docs/legal/` — the MSA, DPA, SCCs, AI Addendum and BAA — but did not alter
 * the public copy on those pages, so their dates deliberately do not move.
 */
export const LEGAL_DOC_DATES: Record<LegalDocSlug, string> = {
  privacy: '2026-08-17',
  terms: '2026-08-17',
  dpa: '2026-07-02',
  cookies: '2026-07-02',
  refunds: '2026-07-02',
  'ai-compliance': '2026-07-02',
  disclosure: '2026-07-02',
};

/** The ISO last-updated date for a legal document. */
export function legalDocDate(slug: LegalDocSlug): string {
  return LEGAL_DOC_DATES[slug];
}
